import { X, Plus, ArrowRight, RotateCw, Upload } from "lucide-react";
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Modal,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from "@material-ui/core";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { toast } from "@/design-system/components/Toast/Toast";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { v4 as uuidv4, NIL as NIL_UUID } from "uuid";

import useListAllDestinationsForEnv from "@/destinations/v2/hooks/useListAllDestinationsForEnv";

import { Button } from "@/design-system/components/Button/Button";

import { fetchSources } from "../../actions/sources";
import useAccountMetadata from "../../hooks/useAccountMetadata";
import { isAccountInHIPAAMode, makeRequest } from "../../util";
import {
  getConfigFieldValidationError,
  getDestinationConfigValidationErrors,
} from "../validation";
import { useModalStyles } from "./styles";

function randomBytes(bytes) {
  return Array.from(crypto.getRandomValues(new Uint8Array(bytes)))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function doesConditionValueMatch(currentValue, conditionValue) {
  if (Array.isArray(conditionValue)) {
    return conditionValue.includes(currentValue);
  }
  return currentValue === conditionValue;
}

/**
 * TODO: This function is very complex and has too many responsibilities. However, legacy frontend persistence logic for
 * saving destinations is subtle, and in the short term it's best to reuse existing logic instead of trying to carefully
 * refactor it or replace it with brand new code.
 */
export function makeDestSaveFunction(
  dest,
  settings,
  existingConfig,
  values,
  onSave,
  onFail,
  handleClose,
  accountMetadata,
) {
  if (!accountMetadata.loaded) {
    return () => {};
  }
  const handleSave = async () => {
    const fieldSettings = _(settings)
      .extend(values)
      .mapValues((value) => (typeof value === "string" ? value.trim() : value))
      .value();

    const validationErrors = getDestinationConfigValidationErrors(
      dest,
      fieldSettings,
    );
    const firstValidationError = Object.values(validationErrors)[0];
    if (firstValidationError) {
      toast.error(firstValidationError);
      onFail();
      return;
    }

    const isExistingInstance =
      existingConfig && existingConfig.id && existingConfig.id != NIL_UUID;
    const id = isExistingInstance ? existingConfig.id : uuidv4();

    const settingsToSave = _(settings)
      .extend(values)
      // Remove any values that are just a blank string as that's a filler value
      // we use from the backend.
      .pickBy((value) => value !== "")
      .value();

    // Occasionally human error while typing/pasting important destination fields may include extra whitespace. We can
    // carefully trim any whitespace from string-typed inputs. Note that we need to be careful about performing this
    // trim operation, because the `settingsToSave` object's values may be of any JavaScript type, not just `string`.
    const settingsToSaveWithoutWhitespace = Object.keys(settingsToSave).reduce(
      (acc, key) => {
        let newValue = settingsToSave[key];

        // strip hyphens from google ads customer ID
        if (dest.slug === "google-ads-audiences" && key === "customerId") {
          newValue = newValue.replace(/[^\d]/g, "");
        }

        return {
          ...acc,
          [key]: typeof newValue === "string" ? newValue.trim() : newValue,
        };
      },
      {},
    );

    const destConfig = {
      id: id,
      dest_type: dest.key,
      name: dest.key,
      connection_mode:
        existingConfig.connection_mode ||
        (isAccountInHIPAAMode(accountMetadata) && dest.supportsServerSide
          ? "server"
          : dest.connectionMode),
      settings: settingsToSaveWithoutWhitespace,
      has_baa: existingConfig.has_baa,
      function_id: dest.key === "Function" ? dest.slug : null,
      custom_label: existingConfig.custom_label ?? "",
    };

    if (
      !isExistingInstance &&
      dest.destinationCategories.includes("Audiences")
    ) {
      destConfig.enabled = true;
    }

    try {
      await makeRequest("save_destination_config", destConfig);
      if (
        !isExistingInstance &&
        dest.destinationCategories.includes("Audiences")
      ) {
        await makeRequest("toggle_whole_destination", {
          dest_id: id,
          state: true,
        });
      }
      onSave && onSave(dest, id, destConfig);
      handleClose();
    } catch (error) {
      toast.error(
        error.response?.data.error_message ??
          "Something went wrong. Unable to save destination.",
      );
      console.error(error);
      onFail();
    }
  };
  return handleSave;
}

function makeSourceSaveFunction(
  integration,
  values,
  onSave,
  onErrorMessage,
  handleClose,
  dispatch,
) {
  function handleSave() {
    makeRequest("configure_new_sql_source", {
      source_name: integration.name,
      settings: values,
    })
      .then((resp) => {
        if (resp.data && resp.data.error_message) {
          onErrorMessage(resp.data.error_message);
          return;
        }
        dispatch(fetchSources());
        onSave && onSave();
        handleClose();
      })
      .catch((error) => {
        toast.error("Something went wrong. Unable to save source.");
        console.error(error);
      });
  }

  return handleSave;
}

const ModalField = ({
  field,
  values,
  settings,
  handleFieldChange,
  checkboxEnabled,
  CustomConfigControlComponent,
}) => {
  const classes = useModalStyles();
  const [externalPopup, setExternalPopup] = useState(null);
  const [oauthState, setOauthState] = useState(null);
  const defaultValue = field.defaultValue || null;
  var value;

  function handleOAuth() {
    if (externalPopup) {
      return;
    }
    const width = 500;
    const height = 400;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;
    const title = `Salesforce OAuth`;
    const state = randomBytes(64);
    setOauthState(state);
    const url = field.oauthURL(state, _.extend({}, settings, values));
    const popup = window.open(
      url,
      title,
      `width=${width},height=${height},left=${left},top=${top}`,
    );
    setExternalPopup(popup);
  }

  useEffect(() => {
    if (!externalPopup) {
      return;
    }

    const timer = setInterval(() => {
      if (!externalPopup) {
        timer && clearInterval(timer);
        return;
      }

      // The browser doesn't let you access information related to a cross
      // origin window. We will keep retrying until we are able to access
      // this information.
      var currentUrl;
      try {
        currentUrl = externalPopup.location.href;
      } catch (e) {
        return;
      }
      if (!currentUrl) {
        return;
      }
      const searchParams = new URL(currentUrl).searchParams;
      const code = searchParams.get("code");
      if (code) {
        externalPopup.close();
        setExternalPopup(null);
        timer && clearInterval(timer);

        if (searchParams.get("state") !== oauthState) {
          toast.error("OAuth Failed");
          return;
        }

        makeRequest("get_oauth_token", {
          integration_name: field.integration_name,
          code: code,
        })
          .then((resp) => {
            handleFieldChange(field.key, resp.data);
            toast.success("OAuth Completed");
          })
          .catch((error) => {
            console.error(error);
            toast.error("OAuth Failed");
          });
      }
    }, 500);
  }, [
    externalPopup,
    field.integration_name,
    field.key,
    handleFieldChange,
    oauthState,
  ]);

  if (field.sensitive) {
    if (field.key in values) {
      value = values[field.key];
    } else if (field.key in settings) {
      // If the user hasn't edited the field, but there is an existing value
      // for it, show a bunch of asterisks.
      value = "********";
    } else {
      value = "";
    }
  } else {
    value =
      values[field.key] === undefined || values[field.key] === null
        ? settings[field.key]
        : values[field.key];

    if (value === undefined || value === null) {
      value = defaultValue;
    }
  }

  if (CustomConfigControlComponent) {
    return (
      <FormControl
        component="fieldset"
        className={clsx("my-5", "w-full", classes.formControl)}
      >
        <FormGroup>
          <FormControlLabel
            className={clsx(classes.formControlLabel)}
            control={
              <CustomConfigControlComponent
                field={field}
                value={value}
                onChange={handleFieldChange}
              />
            }
          />
        </FormGroup>
        {field.description && (
          <FormHelperText>
            <div className="text-textDefault -mt-1 text-xs leading-4">
              {field.description}
            </div>
          </FormHelperText>
        )}
      </FormControl>
    );
  }
  if (field.type && field.type === "boolean") {
    return (
      <FormControl
        component="fieldset"
        className={clsx("my-5", "w-full", classes.formControl)}
      >
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                className="m-1"
                checked={value}
                disabled={!checkboxEnabled}
                onChange={(event) =>
                  handleFieldChange(event.target.name, event.target.checked)
                }
                name={field.key}
              />
            }
            label={field.name}
          />
        </FormGroup>
        {field.description && (
          <FormHelperText component="div">
            <div className="text-textDefault -mt-1 text-xs leading-4">
              {field.description}
            </div>
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  if (field.type && field.type === "keyValueList") {
    return (
      <FormControl
        component="fieldset"
        className={clsx("my-5", "w-full", classes.formControl)}
      >
        <p className="text-sm">
          <b>{field.name}</b>
        </p>
        <FormGroup>
          <KeyValueMappingControl
            field={field}
            value={value}
            onChange={handleFieldChange}
            keyLabel={
              (field.keyValueListLabels && field.keyValueListLabels.keyLabel) ||
              "key"
            }
            valueLabel={
              (field.keyValueListLabels &&
                field.keyValueListLabels.valueLabel) ||
              "value"
            }
            objectLabel={
              (field.keyValueListLabels &&
                field.keyValueListLabels.objectLabel) ||
              "entry"
            }
          />
        </FormGroup>
        {field.description && (
          <FormHelperText component="div">
            <div className="text-textDefault -mt-1 text-xs leading-4">
              {field.description}
            </div>
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  if (field.type && field.type === "select") {
    const options = field.options.map((opt) => ({
      value: opt[1],
      label: opt[0],
    }));
    const selectedOption = options.find((opt) => opt.value === value);

    return (
      <FormControl
        component="fieldset"
        className={clsx(
          "my-5",
          "w-full",
          classes.formControl,
          classes.formControlSelect,
        )}
      >
        <FormGroup>
          <label htmlFor={field.key}>{field.name}</label>
          <Select
            options={options}
            className="m-1 w-full"
            value={selectedOption}
            onChange={(option) => handleFieldChange(field.key, option.value)}
            styles={{
              menu: (provided) => ({
                ...provided,
                zIndex: 9999,
                backgroundColor: "#FFF",
              }),
            }}
            name={field.key}
          />
        </FormGroup>
        {field.description && (
          <FormHelperText component="div">
            <div className="text-textDefault -mt-1 text-xs leading-4">
              {field.description}
            </div>
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  if (field.type && field.type === "radio") {
    const options = field.options;
    // Use the current value, defaultValue, or first option as fallback
    const effectiveValue = value ?? field.defaultValue ?? options[0]?.value;
    const selectedOption = options.find((opt) => opt.value === effectiveValue);

    return (
      <FormControl
        component="fieldset"
        className={clsx("my-5", "w-full", classes.formControl)}
      >
        <FormGroup>
          <FormControlLabel
            control={
              <RadioGroup
                name={field.key}
                value={selectedOption?.value ?? ""}
                onChange={(event) =>
                  handleFieldChange(event.target.name, event.target.value)
                }
              >
                {options.map((option, i) => {
                  return (
                    <FormControlLabel
                      key={i}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                      disabled={option.disabled}
                    />
                  );
                })}
              </RadioGroup>
            }
          />
        </FormGroup>
        {field.description && (
          <FormHelperText component="div">
            <div className="text-textDefault -mt-1 text-xs leading-4">
              {field.description}
            </div>
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  if (field.type === "oauth") {
    return (
      <FormControl>
        <div className="mt-2">
          <Button onClick={handleOAuth}>
            {value ? "Reauthenticate OAuth" : "Authenticate OAuth"}
          </Button>
        </div>
      </FormControl>
    );
  }

  const fieldSettings = _.extend({}, settings, values);
  const validationError = getConfigFieldValidationError(field, fieldSettings);

  return (
    <FormControl
      component="fieldset"
      className={clsx("my-5", "w-full", classes.formControl)}
    >
      <FormGroup>
        <FormControlLabel
          className={clsx(classes.formControlLabel)}
          control={
            <TextField
              fullWidth
              autoComplete="off"
              label={field.name}
              name={field.key}
              placeholder={field.placeholder}
              onChange={(event) => {
                if (field.type === "number") {
                  handleFieldChange(
                    event.target.name,
                    event.target.valueAsNumber,
                  );
                } else {
                  handleFieldChange(event.target.name, event.target.value);
                }
              }}
              // If the user previously configured this field we want to display
              // asterisks when they go to configure it.
              value={value || ""}
              key={field.name}
              variant="outlined"
              type={field.type || "text"}
              className={clsx(classes.inputField, "fs-exclude mb-2")}
              required={
                field.required ||
                (field.requiredWhen &&
                  doesConditionValueMatch(
                    values[field.requiredWhen.field],
                    field.requiredWhen.value,
                  ))
              }
              error={!!validationError}
              helperText={validationError}
            />
          }
        />
      </FormGroup>
      {field.description && (
        <FormHelperText component="div">
          <div className="text-textDefault -mt-1 text-xs leading-4">
            {field.description}
          </div>
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const getDefaultValuesFromConfigFields = (configFields) => {
  return Object.fromEntries(
    configFields
      .filter((field) => field.defaultValue !== undefined)
      .map((field) => {
        let value;
        switch (field.type) {
          case "number":
            value = parseInt(field.defaultValue);
            break;
          case "boolean":
            value = !!field.defaultValue || false;
            break;
          case "keyValueList":
            value = "defaultValue" in field ? field.defaultValue : [];
            break;
          case "list":
            value = [];
            break;
          default:
            value = field.defaultValue || "";
        }

        return [field.key, value];
      }),
  );
};

export const getNonSensitiveSettings = (settings, configFields) => {
  return Object.fromEntries(
    Object.keys(settings)
      .filter((fieldKey) => {
        const fieldConfig = configFields.find((f) => f.key === fieldKey);
        if (!fieldConfig) {
          return false;
        }
        return !fieldConfig.sensitive;
      })
      .map((fieldKey) => {
        const fieldConfig = configFields.find((f) => f.key === fieldKey);

        const mappingFunction = fieldConfig.mapConfigValueToControlValue;
        if (mappingFunction) {
          // Pass full settings as second param for fields that need to derive value from other fields
          return [fieldKey, mappingFunction(settings[fieldKey], settings)];
        } else {
          return [fieldKey, settings[fieldKey]];
        }
      }),
  );
};

const SOURCE_INTEGRATION = "source";
const DEST_INTEGRATION = "destination";

const ConfigureIntegrationsModal = ({
  open,
  onClose,
  onSave,
  integration,
  integrationType,
  includeHeader = false,
  settings,
  limitSettings = null,
  settingsToControl = {},
  existingConfig = {},
  title = "Integration",
  valuesStateObjFromParent = null,
  overrideHeader = null,
}) => {
  // Ok, pay attention, this is tricky.
  // `settings` is the settings sent from our server. This usually does not include any sensitive information (i.e. secret keys)
  // `defaultValueMap` is a base layer of default values that comes from the destination config
  // `values` (defined later) is meant to be the current state of values that we show to the users.
  //
  // Where this gets tricky is sensitive values... how do we avoid showing them & sending them from the server in a
  // way that we still know when it's already been filled in and can be replaced?
  //
  // The way we do this is by sending an empty string, and any field marked as "sensitive" in the dest.configFields will
  //  be treated differently
  // First thing we do is filter those values out of the `settings` into `nonSensitiveSettings` object.
  // So now we have `nonSensitiveSettings` which is the values that are safe to display to the user, and `settings`
  //  which will differ from `nonSensitiveSettings` only in the sensitive values.
  //
  // We initialize `values` with the `nonSensitiveSettings` layered on top of the `defaultValueMap`
  // To the field component, we pass all of these in.
  // That component will attempt to display in order:
  // - If the field is sensitive & in `values`, the user has entered it after page load, so we display it as-is.
  // - If the field is sensitive & not in `values` & is in `settings`, this means it's been set, so display something like "***"
  // - If the field is sensitive & not in `values` & not in `settings`, it hasn't been set

  const defaultValueMap = getDefaultValuesFromConfigFields(
    integration.configFields,
  );

  const nonSensitiveSettings = getNonSensitiveSettings(
    settings,
    integration.configFields,
  );

  const { refetch: refetchDestinations } = useListAllDestinationsForEnv();
  const classes = useModalStyles();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState(null);
  let valuesObj = useState({
    ...defaultValueMap,
    ...nonSensitiveSettings,
  });
  if (valuesStateObjFromParent) {
    valuesObj = valuesStateObjFromParent;
  }
  const [values, setValues] = valuesObj;
  const [saving, setSaving] = useState(false);

  const handleFieldChange = (key, value) => {
    setValues((currentValues) => ({
      ...defaultValueMap,
      ...currentValues,
      [key]: value,
    }));
  };

  let headerContent = null;
  if (overrideHeader) {
    headerContent = overrideHeader;
  } else if (includeHeader) {
    headerContent = <p className="text-sm">{integration.configHelp}</p>;
  }

  const onFail = () => {
    setSaving(false);
  };

  const handleClose = () => {
    setValues({});
    onClose();
  };

  const accountMetadata = useAccountMetadata();

  var saveFn;
  if (integrationType === DEST_INTEGRATION) {
    const mappedValues = _.mapValues(values, (v, k) => {
      const configField = integration.configFields.find((f) => f.key === k);

      const mapControlValueToConfigValue =
        configField.mapControlValueToConfigValue;
      if (mapControlValueToConfigValue) {
        return mapControlValueToConfigValue(v);
      } else {
        return v;
      }
    });
    saveFn = makeDestSaveFunction(
      integration,
      settings,
      existingConfig,
      mappedValues,
      onSave,
      onFail,
      handleClose,
      accountMetadata,
    );
  } else {
    saveFn = makeSourceSaveFunction(
      integration,
      _.extend({}, settings, values),
      onSave,
      setErrorMessage,
      handleClose,
      dispatch,
    );
  }

  const handleSave = () => {
    const fieldSettings = _.extend(
      {},
      defaultValueMap,
      nonSensitiveSettings,
      values,
    );
    const validationErrors = getDestinationConfigValidationErrors(
      integration,
      fieldSettings,
    );
    const firstValidationError = Object.values(validationErrors)[0];
    if (firstValidationError) {
      setErrorMessage(firstValidationError);
      return;
    }

    setErrorMessage(null);
    setSaving(true);
    saveFn();
    refetchDestinations();
  };

  if (!open) {
    return null;
  }

  const fieldSettingsForValidation = _.extend(
    {},
    defaultValueMap,
    nonSensitiveSettings,
    values,
  );
  const validationErrors = getDestinationConfigValidationErrors(
    integration,
    fieldSettingsForValidation,
  );
  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  const renderableFields = integration.configFields.filter((field) => {
    return !limitSettings || limitSettings.indexOf(field.key) !== -1;
  });

  const textFields = renderableFields
    .filter((field) => {
      // Check enabledIfCheckbox condition
      if (field.enabledIfCheckbox) {
        return !!values[field.enabledIfCheckbox];
      }
      // Check visibleWhen condition (for radio/select conditional fields)
      if (field.visibleWhen) {
        const { field: conditionField, value: conditionValue } =
          field.visibleWhen;
        const currentValue = values[conditionField];
        if (Array.isArray(conditionValue)) {
          return conditionValue.includes(currentValue);
        }
        return currentValue === conditionValue;
      }
      return true;
    })
    .map((field, i) => (
      <ModalField
        key={i}
        field={field}
        values={values}
        settings={settings}
        handleFieldChange={handleFieldChange}
        checkboxEnabled={true}
        CustomConfigControlComponent={settingsToControl[field.key]}
      />
    ));

  return (
    <Modal onClose={handleClose} open={open}>
      <Card className={clsx(classes.root, classes.smallModal)}>
        <CardHeader
          title={`${title} Settings: ${integration.name}`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<X onClick={onClose} />}
        />
        <Divider />
        <CardContent className={classes.modalContent}>
          {headerContent}
          {textFields}
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <p className="mr-auto overflow-auto text-xs text-red-600">
            {errorMessage}
          </p>
          <Button variant="ghost" onClick={handleClose}>
            Close
          </Button>
          {onSave && (
            <>
              <Button
                onClick={handleSave}
                disabled={hasValidationErrors || saving}
              >
                Save
              </Button>
              <RotateCw
                className={clsx("mr-2 animate-spin", !saving && "invisible")}
              />
            </>
          )}
        </CardActions>
      </Card>
    </Modal>
  );
};

ConfigureIntegrationsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  dest: PropTypes.object,
  configFields: PropTypes.array,
  includeHeader: PropTypes.bool,
  settings: PropTypes.object.isRequired,
  limitSettings: PropTypes.arrayOf(PropTypes.string),
};

const stripQuotes = (str) => {
  if (str.length >= 2 && str.startsWith('"') && str.endsWith('"')) {
    return str.slice(1, -1).replace(/""/g, '"');
  }
  return str;
};

const parseCsvMappings = (csvText, columnMapping) => {
  const lines = csvText.split("\n").filter((line) => line.trim() !== "");
  if (lines.length < 2) return [];

  const headers = lines[0]
    .split(",")
    .map((h) => stripQuotes(h.trim()).toLowerCase());
  const keyCol = headers.indexOf(columnMapping.key.toLowerCase());
  const valueCol = headers.indexOf(columnMapping.value.toLowerCase());
  if (keyCol === -1 || valueCol === -1) return [];

  const mappings = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(",").map((c) => stripQuotes(c.trim()));
    const key = cols[keyCol];
    const value = cols[valueCol];
    if (key && value) {
      mappings.push({ key, value });
    }
  }
  return mappings;
};

const KeyValueMappingControl = ({
  field,
  value: mappingValues = [],
  onChange,
  keyLabel,
  valueLabel,
  objectLabel,
}) => {
  const classes = useModalStyles();

  const onMappingChange = (index, { key, value }) => {
    const newValue = mappingValues.slice();
    newValue[index] = _.merge(
      { key: mappingValues[index].key, value: mappingValues[index].value },
      { key, value },
    );
    onChange(field.key, newValue);
  };

  const onDelete = (index) => {
    const newValue = mappingValues.slice();
    newValue.splice(index, 1);
    onChange(field.key, newValue);
  };

  const onCsvUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvText = e.target?.result;
      if (typeof csvText !== "string") return;

      const newMappings = parseCsvMappings(csvText, field.csvColumnMapping);
      if (newMappings.length === 0) {
        toast.error("No valid mappings found in CSV");
        return;
      }

      const existingKeys = new Set(mappingValues.map((m) => m.key));
      const deduped = newMappings.filter((m) => !existingKeys.has(m.key));
      onChange(field.key, [...mappingValues, ...deduped]);
      toast.success(`Imported ${deduped.length} mapping(s) from CSV`);
    };
    reader.readAsText(file);

    event.target.value = "";
  };

  return (
    <div className="justify-items-stretch" style={{ width: "100%" }}>
      {mappingValues.map((kvPair, index) => {
        return (
          <div className="flex items-stretch" key={index}>
            <span className="justify-start-items-center flex">
              <TextField
                fullWidth
                autoComplete="off"
                label={keyLabel}
                name={keyLabel}
                onChange={(event) =>
                  onMappingChange(index, { key: event.target.value })
                }
                value={kvPair.key || ""}
                key={field.name}
                variant="outlined"
                type="text"
                inputProps={{ style: { fontFamily: "monospace" } }}
                className={clsx(classes.inputField, "fs-exclude mb-2")}
              />
            </span>
            <span
              className={clsx(
                classes.keyValueListIconContainer,
                "flex items-center justify-self-center",
              )}
            >
              <ArrowRight />
            </span>
            <span className="justify-start-items-center flex">
              <TextField
                fullWidth
                autoComplete="off"
                label={valueLabel}
                name={valueLabel}
                onChange={(event) =>
                  onMappingChange(index, { value: event.target.value })
                }
                value={kvPair.value || ""}
                key={field.name}
                variant="outlined"
                type="text"
                inputProps={{ style: { fontFamily: "monospace" } }}
                className={clsx(classes.inputField, "fs-exclude mb-2")}
              />
            </span>
            <span
              className={clsx(
                classes.keyValueListIconContainer,
                "flex flex-wrap justify-end",
              )}
            >
              <Tooltip title={"Delete " + objectLabel}>
                <div
                  className="hover:bg-navy-200 flex items-center justify-center rounded-full p-1"
                  onClick={() => onDelete(index)}
                >
                  <X className={classes.closeIcon} />
                </div>
              </Tooltip>
            </span>
          </div>
        );
      })}
      <div className="flex gap-2">
        <Button
          onClick={() =>
            onChange(field.key, [...mappingValues, { key: "", value: "" }])
          }
          trailingIcon={<Plus />}
        >
          Add {objectLabel}
        </Button>
        {field.supportsCsvUpload && field.csvColumnMapping && (
          <>
            <input
              type="file"
              accept=".csv"
              id={`csv-upload-${field.key}`}
              style={{ display: "none" }}
              onChange={onCsvUpload}
            />
            <Button
              variant="secondary"
              onClick={() =>
                document.getElementById(`csv-upload-${field.key}`).click()
              }
              trailingIcon={<Upload />}
            >
              Upload CSV
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export {
  ConfigureIntegrationsModal,
  KeyValueMappingControl,
  ModalField,
  DEST_INTEGRATION,
};
